import React, { useState } from "react";
import { useFetch } from "../Hooks/useFetch";
import { useSearchParams } from "../Hooks/useSearchParams";
import { IdentifierMappingContext } from "./IdentifierMappingContext";
import { IdentifierMappingDraw } from "./IdentifierMappingDraw";
import { PharmacyGroupList } from "./PharmacyGroupList";
import { PharmacyGroupDetails } from "./PharmacyGroupDetails/PharmacyGroupDetails";
import { AddIdentifierModal } from "./Modals/AddIdentifierModal";
import { TransferIdentifierModal } from "./Modals/TransferIdentifierModal";
import { Alert, Snackbar } from "@mui/material";
import { pharmacyGroupSort } from "../utils/Helpers";
import { withTheme } from "../common/ThemeProviderWrapper";
import { useDebounce } from "use-debounce";
import { TransferAllIdentifiersModal } from "./Modals/TransferAllIdentifiersModal";
import { CategorizeIdentifierModal } from "./Modals/CategorizeIdentifierModal";
import { CategorizeAllIdentifiersModal } from "./Modals/CategorizeAllIdentifierModal";
import { RemoveIdentifierModal } from "./Modals/RemoveIdentifierModal";

function IdentifierMappingPage({
  purchaseValidationPath,
  coveredEntityNames,
  coveredEntity340bIds,
}) {
  const [searchParams] = useSearchParams();

  // modals & notifications
  const [addIdentifierModalOpen, setAddIdentifierModalOpen] = useState(false);
  const [categorizeIdentifierModalOpen, setCategorizeIdentifierModalOpen] = useState(false);
  const [categorizeAllIdentifiersModalOpen, setCategorizeAllIdentifiersModalOpen] = useState(false);
  const [removeIdentifierModalOpen, setRemoveIdentifierModalOpen] = useState(false);
  const [transferIdentifierModalOpen, setTransferIdentifierModalOpen] = useState(false);
  const [transferAllIdentifiersModalOpen, setTransferAllIdentifiersModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState();

  // filter and tabs
  const [pharmacyGroupTab, setPharmacyGroupTab] = useState("pharmacies");
  const [pharmacyGroupSearch, setPharmacyGroupSearch] = useState(
    searchParams["pharmacy_group_search"] ?? ""
  );
  const [activePharmacyGroup, setActivePharmacyGroup] = useState(
    searchParams["pharmacy_group"] ?? null
  );
  const [coveredEntity340bId, setCoveredEntity340bId] = useState(
    searchParams["id_340b"] ?? coveredEntity340bIds[0]
  );

  // fetch the details of a specific pharmacy group
  const pharmacyGroupDetailsFetcher = useFetch(
    activePharmacyGroup
      ? `/api/covered_entities/${coveredEntity340bId}/pharmacy_groups/${encodeURIComponent(activePharmacyGroup)}`
      : undefined
  );

  const [delayedPharmacyGroupSearch] = useDebounce(pharmacyGroupSearch, 300);

  // fetch all pharmacy groups
  const pharmacyGroupsFetcher = useFetch({
    url: `/api/covered_entities/${coveredEntity340bId}/pharmacy_groups?search=${encodeURIComponent(delayedPharmacyGroupSearch)}`,
    onSuccess: ({ pharmacy_groups }) => {
      // auto select active pharmacy group if not selected.
      if (!activePharmacyGroup && pharmacy_groups.length > 0) {
        setActivePharmacyGroup(pharmacy_groups.sort(pharmacyGroupSort)[0].name);
      }
    },
  });

  const pharmacyGroups = pharmacyGroupsFetcher.data?.pharmacy_groups ?? [];
  // keeps a master list of group names, regardless of search.
  const allGroupNames = pharmacyGroupsFetcher.data?.all_group_names ?? [];

  const contextValues = {
    allGroupNames,
    pharmacyGroupTab,
    setPharmacyGroupTab,
    coveredEntityNames,
    coveredEntity340bIds,
    snackbar,
    setSnackbar,
    pharmacyGroupSearch,
    setPharmacyGroupSearch,
    pharmacyGroups,
    pharmacyGroupsFetcher,
    pharmacyGroupDetailsFetcher,
    coveredEntity340bId,
    setCoveredEntity340bId,
    activePharmacyGroup,
    setActivePharmacyGroup,
    addIdentifierModalOpen,
    setAddIdentifierModalOpen,
    transferIdentifierModalOpen,
    setTransferIdentifierModalOpen,
    transferAllIdentifiersModalOpen,
    setTransferAllIdentifiersModalOpen,
    categorizeIdentifierModalOpen,
    setCategorizeIdentifierModalOpen,
    categorizeAllIdentifiersModalOpen,
    setCategorizeAllIdentifiersModalOpen,
    removeIdentifierModalOpen,
    setRemoveIdentifierModalOpen,
  };

  return (
    <IdentifierMappingContext.Provider value={contextValues}>
      <div className="flex">
        <div className="content__container flex-grow">
          <div className="page-details__container mb-0">
            <div className="page-details__container__info">
              <div className="breadcrumbs__container">
                <a
                  className="breadcrumbs__crumb"
                  href={`${purchaseValidationPath}?id_340b=${coveredEntity340bId}`}
                >{`Purchase Validation >`}</a>
                <div className="breadcrumbs__crumb breadcrumbs__crumb__active">
                  Account Identifier Mappings
                </div>
              </div>
              <div>
                <div className="page-details__title mt-2 mb-2">Account Identifier Mappings</div>
                <div className="t--md">
                  View and manage how we map transactions to your accounts via identifier mapping.
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap--16 mt-4">
            <div
              className="border--rounded"
              style={{ minWidth: 305, height: "75vh", overflowY: "auto" }}
            >
              <PharmacyGroupList />
            </div>
            <div className="w--full">
              <PharmacyGroupDetails />
            </div>
          </div>
        </div>
        <IdentifierMappingDraw />

        <Snackbar
          autoHideDuration={5000}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          onClose={() => setSnackbar()}
          open={!!snackbar}
        >
          {snackbar ? (
            <Alert elevation={5} variant="filled" severity={snackbar?.severity}>
              {snackbar?.message}
            </Alert>
          ) : (
            /* div fixes flicker glitch */
            <div />
          )}
        </Snackbar>
      </div>

      <AddIdentifierModal />
      <TransferIdentifierModal />
      <TransferAllIdentifiersModal />
      <CategorizeIdentifierModal />
      <CategorizeAllIdentifiersModal />
      <RemoveIdentifierModal />
    </IdentifierMappingContext.Provider>
  );
}

export default withTheme(IdentifierMappingPage);
